import instance from '../axios-instance';
import { setProfile } from '../store/profile.slice';
import { setCurrentTenant } from '../store/activeTenant.slice';
import { store } from '../store/store';
import { removeAuthToken } from '../utils/auth-token';

export interface CalendarType {
  user_calendar_id: number;
  calendar_name: string;
  default: number;
  check_availability: number;
}

export interface AccountInfo {
  client_token_id: number;
  key: string;
  primary: boolean;
  email: string;
  needs_reauth?: boolean;
  reauth_message?: string | null;
  calendars: {
    data: CalendarType[];
  };
}

export const getProfile = async (): Promise<IProfile> => {
  const { data } = (await instance.get(`app/profile`)).data;
  store.dispatch(setProfile(data));
  const activeTenant: any = data.tenants.data.filter(
    (tenant: any) => tenant.is_active === true
  );
  store.dispatch(setCurrentTenant(activeTenant[0]));
  return data;
};

export const getCalenders = async () => {
  const { data } = await instance.get(`app/integrations/calendars/list`);
  return data;
};

export const updateProfile = async (data: any) => {
  data.append('_method', 'PUT');
  return await instance.post(`app/profile`, data);
};

export const deleteAccount = async (id: number) => {
  const res = await instance.delete(`app/integrations/${id}`);
  return res;
};

export const updateCalendar = async (data: any) => {
  return await instance.post(
    `app/integrations/calendars/check-availability`,
    data
  );
};

export const addPrimaryCalendar = async (data: any) => {
  return await instance.post(`app/integrations/calendars/set-default`, data);
};

export const checkSyncStatus = async () => {
  return await instance.get(`app/integrations/calendars/sync-status`);
};

export const syncCalender = async () => {
  return await instance.get(`app/integrations/calendars/sync-calender`);
};

/**
 * Refresh profile helper - reloads profile data and updates Redux store
 * Calls getProfile which automatically updates the store via setProfile dispatch
 * Swallows errors to avoid blocking the calling UX flow
 * @returns Promise<IProfile | null> - Returns profile data on success, null on failure
 */
export const refreshProfile = async (): Promise<IProfile | null> => {
  try {
    const profile = await getProfile(); // This already dispatches setProfile to update Redux state
    return profile;
  } catch (error) {
    // Silently log the error without blocking the UX
    console.error('Profile refresh failed:', error);
    return null;
  }
};

export interface IProfile {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  phone: string;
  image?: any;
  email_verified_at: string;
  phone_code: string;
  industry: Industry;
  country: Country;
  permissions: Permissions;
  tenants: Tenants;
  integration: any;
  time_zone_id: string;
  time_zone?: any;
  has_schedule?: boolean;
  status: number;
  role: Role;
  zoho_crm_url?: string;
  domain?: string;
  client_system_Steps: ClientSystemSteps;
  calendar_reauth_required?: boolean;
  calendar_reauth_message?: string | null;
  subscription_info?: SubscriptionInfo;
  subscription_limits?: SubscriptionLimits;
  has_billing_address?: boolean;
  is_payment_active?: boolean;
}

interface SubscriptionInfo {
  plan: string;
  plan_value: number;
  plan_code?: string;
  plan_text?: string;
  plan_type?: string;
  status: string;
  status_value: number | string;
  trial_ends_at: string | null;
  trial_days_remaining: number;
  next_billing_date?: string | null;
  price?: number;
  renewal_days_before_expiry?: number;
  show_deactivate_members?: boolean;
  users_used?: number;
  users_limit?: number;
}

interface SubscriptionLimits {
  events: {
    limit: number;
    used: number;
    can_create: boolean;
    remaining: number;
  };
  clients: {
    limit: number;
    used: number;
    can_create: boolean;
    remaining: number;
  };
  calendar_integrations: {
    limit: number;
    used: number;
    can_add: boolean;
    remaining: number;
    total_tenant: number;
  };
  features: SubscriptionFeatures;
}

export interface SubscriptionFeatures {
  can_create_clients: boolean;
  can_use_group_events: boolean;
  can_use_collective_events: boolean;
  can_use_voting_events: boolean;
  can_use_teams: boolean;
  can_use_round_robin_events: boolean;
  can_use_workflows: boolean;
  can_use_crm: boolean;
  can_use_payments: boolean;
  can_use_custom_cancellation_policy: boolean;
  can_use_advanced_analytics: boolean;
  can_use_routing_forms: boolean;
  can_use_gmail_integration: boolean;
}

export type FeatureKey = keyof SubscriptionFeatures;

interface ClientSystemSteps {
  create_first_event: boolean;
  create_first_client: boolean;
  integrate_with_calendar: boolean;
  integrate_with_crm: boolean;
  integrate_with_gmail: boolean;
  // create_first_workflow: boolean;
}

interface Tenants {
  data: Datum2[];
}

interface Datum2 {
  id: number;
  name: string;
  industry: Industry;
  country: Country;
  domain: string;
  is_owner: boolean;
  is_active: boolean;
}

interface Role {
  id: number;
  name: string;
  role_value: number;
}

interface Permissions {
  data: Datum[];
}

interface Datum {
  name: string;
}

interface Country {
  id: number;
  name: string;
  code_alpha_2: string;
  code_alpha_3: string;
  currency_code: string;
  currency_symbol: string;
  phone_code: number;
}

interface Industry {
  id: number;
  name: string;
  code: string;
}
